import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { LITRES_PER_USG, unitLabel } from '@jupi/shared'
import { useUnitSystem } from '@jupi/ui'
import type { WorkedWeight } from './useWorkedWeight'

// Fuel flow, time in the air and taxi time. Each starts at the plan's or
// the aircraft's figure (shown beneath) and is the pilot's once typed over.
function Field({
  label,
  unit,
  value,
  defaultText,
  overridden,
  onChange,
}: {
  label: string
  unit: string
  value: number
  defaultText: string
  overridden: boolean
  onChange: (n: number | undefined) => void
}) {
  const { t } = useTranslation()
  const [text, setText] = useState<string | null>(null)
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={text ?? String(Math.round(value * 10) / 10)}
      onFocus={(e) => e.target.select()}
      onBlur={() => setText(null)}
      onChange={(e) => {
        setText(e.target.value)
        const n = Number(e.target.value.replace(',', '.'))
        if (e.target.value.trim() === '') onChange(undefined)
        else if (Number.isFinite(n) && n >= 0) onChange(n)
      }}
      helperText={overridden ? t('plan.worked.overridden', { value: defaultText }) : t('plan.worked.default', { value: defaultText })}
      slotProps={{
        htmlInput: { inputMode: 'decimal' },
        input: { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> },
      }}
    />
  )
}

export default function WbAssumptionsCard({ weight }: { weight: WorkedWeight }) {
  const { t } = useTranslation()
  const system = useUnitSystem()
  const { worked, defaults, overrides, setOverride, reset } = weight
  if (!worked) return null
  const a = worked.assumptions
  const flowDefault = defaults.flowUsgPerHour
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('plan.worked.assumptions')}
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: '1fr' } }}>
          {/* Fuel flow in both units, each editable; typing in one moves the other. */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('plan.worked.fuelFlow')}
            </Typography>
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: '1fr 1fr' }}>
              <Field
                label="l/h"
                unit="l/h"
                value={a.flowUsgPerHour * LITRES_PER_USG}
                defaultText={flowDefault === null ? '–' : (flowDefault * LITRES_PER_USG).toFixed(1)}
                overridden={overrides.flowUsgPerHour !== undefined}
                onChange={(n) => setOverride({ flowUsgPerHour: n === undefined ? undefined : n / LITRES_PER_USG })}
              />
              <Field
                label="USG/h"
                unit="USG/h"
                value={a.flowUsgPerHour}
                defaultText={flowDefault === null ? '–' : flowDefault.toFixed(1)}
                overridden={overrides.flowUsgPerHour !== undefined}
                onChange={(n) => setOverride({ flowUsgPerHour: n })}
              />
            </Box>
          </Box>
          <Field
            label={t('plan.worked.flightTime')}
            unit="min"
            value={a.flightMinutes}
            defaultText={`${defaults.flightMinutes} min`}
            overridden={overrides.flightMinutes !== undefined}
            onChange={(n) => setOverride({ flightMinutes: n })}
          />
          <Field
            label={t('plan.worked.taxiTime')}
            unit="min"
            value={a.taxiMinutes}
            defaultText={`${defaults.taxiMinutes} min`}
            overridden={overrides.taxiMinutes !== undefined}
            onChange={(n) => setOverride({ taxiMinutes: n })}
          />
          <Field
            label={t('plan.worked.taxiFuelRate')}
            unit="l/10 min"
            value={a.taxiLitresPerTenMin}
            defaultText={`${defaults.taxiLitresPerTenMin} l`}
            overridden={overrides.taxiLitresPerTenMin !== undefined}
            onChange={(n) => setOverride({ taxiLitresPerTenMin: n })}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('plan.worked.fuelAssumed', { density: a.density.toFixed(2), mass: unitLabel('mass', system) })}
        </Typography>
        {Object.keys(overrides).length > 0 ? (
          <Button size="small" onClick={reset} sx={{ mt: 1 }}>
            {t('plan.worked.useDefaults')}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}
